/**
 * BrainBuilderDemoPage (`/demo/brain-builder`) — Patch 34.1.10.
 *
 * A guided seven-step Business Brain intake demo that persists ONLY in
 * ``sessionStorage`` — no backend writes, no LLM calls, no cost. The
 * demo shows how a new business teaches Orkexra who it is:
 *
 *   1. Business identity
 *   2. Audience
 *   3. Offers
 *   4. Voice
 *   5. Visual identity
 *   6. Market context
 *   7. Marketing preferences
 *
 * Refresh preserves the current step / answers within the session; a
 * "Restart demo" action clears the isolated state. The final step
 * shows a completion card and an "Explore Ready-to-Work Demo" switch
 * so visitors can see the finished product afterwards.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain, Building2, ChevronLeft, ChevronRight, Megaphone, Palette,
  RotateCcw, Sparkles, Target, TrendingUp, Users,
} from "lucide-react";

import { DemoShellNav } from "@/widgets/DemoShellNav/DemoShellNav";
import { trackDemoEvent } from "@/shared/lib/demoAnalytics";

const STORAGE_KEY = "orkexra.demo.brain-builder.v1";

interface StepDef {
  id: string;
  title: string;
  eyebrow: string;
  icon: JSX.Element;
  fields: Array<{ key: string; label: string; placeholder: string; multiline?: boolean }>;
}

const STEPS: StepDef[] = [
  {
    id: "identity", eyebrow: "Step 1 · Business identity",
    title: "What business are we teaching Orkexra about?",
    icon: <Building2 className="h-4 w-4" />,
    fields: [
      { key: "business_name", label: "Business name", placeholder: "e.g. Aurora Coffee Co." },
      { key: "one_liner", label: "One-line description", placeholder: "The single sentence you'd tell a stranger at dinner." },
      { key: "category", label: "Category", placeholder: "Coffee shop · SaaS · Studio · …" },
    ],
  },
  {
    id: "audience", eyebrow: "Step 2 · Audience",
    title: "Who are you for — and who aren't you for?",
    icon: <Users className="h-4 w-4" />,
    fields: [
      { key: "primary_audience", label: "Primary audience", placeholder: "The person you build for.", multiline: true },
      { key: "not_for", label: "Not for", placeholder: "The people you happily send elsewhere." },
    ],
  },
  {
    id: "offers", eyebrow: "Step 3 · Offers",
    title: "What do you sell — and how is it positioned?",
    icon: <Target className="h-4 w-4" />,
    fields: [
      { key: "core_offers", label: "Core offers", placeholder: "List your headline products or services.", multiline: true },
      { key: "positioning", label: "Positioning", placeholder: "The distinct promise you make." },
    ],
  },
  {
    id: "voice", eyebrow: "Step 4 · Voice",
    title: "How do you sound when you sound like yourself?",
    icon: <Sparkles className="h-4 w-4" />,
    fields: [
      { key: "voice_words", label: "Three words that describe your voice", placeholder: "Warm · Curious · Honest" },
      { key: "voice_avoid", label: "What we should never sound like", placeholder: "Corporate · Salesy · Robotic" },
    ],
  },
  {
    id: "visual", eyebrow: "Step 5 · Visual identity",
    title: "What does your brand look like?",
    icon: <Palette className="h-4 w-4" />,
    fields: [
      { key: "palette", label: "Palette", placeholder: "Cream, espresso, terracotta accents." },
      { key: "aesthetic", label: "Aesthetic notes", placeholder: "Editorial · Slow-craft · Warm light.", multiline: true },
    ],
  },
  {
    id: "market", eyebrow: "Step 6 · Market context",
    title: "What's the landscape you're operating in?",
    icon: <TrendingUp className="h-4 w-4" />,
    fields: [
      { key: "competitors", label: "Notable competitors", placeholder: "Two or three names or archetypes." },
      { key: "market_gap", label: "The gap you fill", placeholder: "Where you win when they don't." },
    ],
  },
  {
    id: "marketing", eyebrow: "Step 7 · Marketing preferences",
    title: "How do you want your marketing to run?",
    icon: <Megaphone className="h-4 w-4" />,
    fields: [
      { key: "cadence", label: "Publishing cadence", placeholder: "Weekly · Twice weekly · Daily" },
      { key: "approval_style", label: "Approval style", placeholder: "Approve every campaign · Autopilot with weekly review · …" },
    ],
  },
];

interface DemoState {
  step: number; // 0-6
  answers: Record<string, string>;
  completed: boolean;
}

function loadState(): DemoState {
  if (typeof window === "undefined") return { step: 0, answers: {}, completed: false };
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as DemoState;
  } catch {
    // ignore corrupted state
  }
  return { step: 0, answers: {}, completed: false };
}

function saveState(state: DemoState): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage may be blocked in some private modes — proceed without persistence.
  }
}

export default function BrainBuilderDemoPage(): JSX.Element {
  const [state, setState] = useState<DemoState>(() => loadState());

  useEffect(() => {
    trackDemoEvent("brain_builder_demo_opened", "/demo/brain-builder");
  }, []);

  useEffect(() => {
    saveState(state);
  }, [state]);

  const step = useMemo(() => STEPS[state.step] ?? STEPS[0], [state.step]);
  const progress = Math.round(((state.step + (state.completed ? 1 : 0)) / STEPS.length) * 100);
  const answersFilled = step.fields.every((f) => (state.answers[f.key] ?? "").trim().length > 0);

  const setAnswer = (key: string, value: string): void =>
    setState((s) => ({ ...s, answers: { ...s.answers, [key]: value } }));

  const next = (): void => {
    if (state.step >= STEPS.length - 1) {
      setState((s) => ({ ...s, completed: true }));
      return;
    }
    setState((s) => ({ ...s, step: s.step + 1 }));
  };
  const prev = (): void => setState((s) => ({ ...s, step: Math.max(0, s.step - 1) }));
  const restart = (): void => {
    if (typeof window !== "undefined") sessionStorage.removeItem(STORAGE_KEY);
    setState({ step: 0, answers: {}, completed: false });
  };

  return (
    <div
      data-testid="brain-builder-demo"
      className="min-h-screen bg-[var(--md-ink-0)] text-[var(--md-ivory-0)]"
    >
      <DemoShellNav current="brain-builder" />

      <main className="mx-auto max-w-[880px] px-6 py-10 md:py-14">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="md-eyebrow md-eyebrow--amber inline-flex items-center gap-2">
              <Brain className="h-3.5 w-3.5" /> Brain Builder · Demo mode
            </div>
            <h1 className="mt-3 font-display italic text-3xl md:text-4xl text-[var(--md-ivory-0)]">
              Teach Orkexra your business.
            </h1>
            <p className="mt-2 max-w-lg text-sm text-[var(--md-fog)]">
              Seven short steps. Nothing you type here leaves the demo — this
              session persists locally only and can be reset any time.
            </p>
          </div>
          <button
            onClick={restart}
            data-testid="brain-builder-restart"
            className="md-btn md-btn--ghost inline-flex text-xs"
            type="button"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Restart demo
          </button>
        </div>

        {/* Progress rail */}
        <div className="mt-6" data-testid="brain-builder-progress">
          <div className="flex items-center justify-between text-[11px] text-[var(--md-fog)]">
            <span>Step {Math.min(state.step + 1, STEPS.length)} of {STEPS.length}</span>
            <span>{progress}% complete</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--md-ink-2)]">
            <div
              className="h-full rounded-full bg-[var(--md-amber)] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {state.completed ? (
          <CompletionCard onRestart={restart} answers={state.answers} />
        ) : (
          <section
            data-testid={`brain-builder-step-${step.id}`}
            className="mt-8 rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-6 md:p-8"
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden
                className="grid h-9 w-9 place-items-center rounded-lg border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/10 text-[var(--md-amber)]"
              >
                {step.icon}
              </span>
              <div>
                <div className="md-eyebrow md-eyebrow--amber">{step.eyebrow}</div>
                <h2 className="mt-1 font-display italic text-xl md:text-2xl text-[var(--md-ivory-0)]">
                  {step.title}
                </h2>
              </div>
            </div>

            <div className="mt-6 grid gap-4">
              {step.fields.map((f) => (
                <label key={f.key} className="block">
                  <span className="md-eyebrow">{f.label}</span>
                  {f.multiline ? (
                    <textarea
                      data-testid={`brain-builder-input-${f.key}`}
                      value={state.answers[f.key] ?? ""}
                      onChange={(e) => setAnswer(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      rows={3}
                      className="mt-2 w-full rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-0)] px-3 py-2 text-sm text-[var(--md-ivory-0)] placeholder-[color:var(--md-fog)]/60 focus:border-[color:var(--md-amber)]/60 focus:outline-none"
                    />
                  ) : (
                    <input
                      data-testid={`brain-builder-input-${f.key}`}
                      value={state.answers[f.key] ?? ""}
                      onChange={(e) => setAnswer(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="mt-2 w-full rounded-lg border border-[color:var(--md-line)] bg-[var(--md-ink-0)] px-3 py-2 text-sm text-[var(--md-ivory-0)] placeholder-[color:var(--md-fog)]/60 focus:border-[color:var(--md-amber)]/60 focus:outline-none"
                    />
                  )}
                </label>
              ))}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={prev}
                disabled={state.step === 0}
                data-testid="brain-builder-prev"
                className="md-btn md-btn--ghost inline-flex disabled:opacity-30"
                type="button"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
              <button
                onClick={next}
                disabled={!answersFilled}
                data-testid="brain-builder-next"
                className="md-btn md-btn--primary inline-flex disabled:opacity-40"
                type="button"
              >
                {state.step === STEPS.length - 1 ? "Complete the Brain" : "Continue"}
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </section>
        )}

        <p className="mt-8 text-center text-[11px] text-[var(--md-fog)]/70">
          Demo mode · Nothing typed here is sent to Orkexra servers. AI
          generation, exports, uploads, and provider metadata are disabled.
        </p>
      </main>
    </div>
  );
}

function CompletionCard({
  onRestart,
  answers,
}: {
  onRestart: () => void;
  answers: Record<string, string>;
}): JSX.Element {
  return (
    <section
      data-testid="brain-builder-complete"
      className="mt-8 rounded-2xl border border-[color:var(--md-amber)]/40 bg-gradient-to-b from-[var(--md-amber)]/10 to-transparent p-6 md:p-8"
    >
      <div className="md-eyebrow md-eyebrow--amber">Business Brain · Draft complete</div>
      <h2 className="mt-2 font-display italic text-2xl md:text-3xl text-[var(--md-ivory-0)]">
        Orkexra now knows{" "}
        <span style={{ color: "var(--md-amber)" }}>
          {answers.business_name?.trim() || "your business"}
        </span>
        .
      </h2>
      <p className="mt-3 max-w-lg text-sm text-[var(--md-fog)]">
        In a real workspace, this Brain becomes the shared source of truth
        for every campaign, asset, and approval Orkexra prepares — so the
        marketing always sounds like the brand.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <Link
          to="/demo/workspace"
          data-testid="completion-workspace-cta"
          onClick={() => trackDemoEvent("demo_switched", "/demo/brain-builder")}
          className="md-btn md-btn--primary inline-flex justify-center"
        >
          Explore Ready-to-Work Demo <ChevronRight className="h-3.5 w-3.5" />
        </Link>
        <button
          onClick={onRestart}
          data-testid="completion-restart"
          className="md-btn md-btn--ghost inline-flex justify-center"
          type="button"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Start over
        </button>
      </div>
    </section>
  );
}
